import httpClient from ".";

import type { IUserInfo } from "@/hooks/use-user-info";
import type Speciality from "@/model/speciality";

import type { ICostEstFilterParams } from "@/hooks/use-cost-est-filter";
import type { SpendSummaryAccumulatorWithTier } from "@/model/spend-summary";
import { DEFAULT_NETWORK_TYPE } from "@/constants";

const procedureSearch = async (
  queryString: string,
  userInfo: IUserInfo,
  url?: string,
) => {
  const data = {
    memberId: userInfo?.memberId,
    groupId: userInfo?.groupId,
    divisionId: userInfo?.divisionId,
    planId: userInfo?.planId,
  };

  const config = {
    method: "post",
    maxBodyLength: Infinity,
    url: url
      ? url
      : `/free-text/billingCode?offset=0&limit=10&q=${queryString}`,
    headers: {
      Authorization: `Bearer ${userInfo.accessToken}`,
      GroupId: userInfo?.groupId,
    },
    data,
  };

  return httpClient.request(config).then((response) => {
    return {
      next: response?.data?.next || "",
      data: response?.data?.results?.data || [],
    };
  });
};

const facilitySearch = async (
  queryString: string,
  userInfo: IUserInfo,
  filter: ICostEstFilterParams,
  url?: string,
) => {
  const data = {
    memberId: userInfo?.memberId,
    groupId: userInfo?.groupId,
    divisionId: userInfo?.divisionId,
    planId: userInfo?.planId,
    network: DEFAULT_NETWORK_TYPE.NETWORK_NAME,
    lat: filter.targetLocation?.lat ?? "",
    lon: filter.targetLocation?.lng ?? "",
    distance: filter.distance?.value ?? null,
  };

  const config = {
    method: "post",
    maxBodyLength: Infinity,
    url: url
      ? url
      : `/free-text/${filter.procedure?.code}/filter/facility/?offset=0&limit=10&q=${queryString}`,
    headers: {
      Authorization: `Bearer ${userInfo?.accessToken}`,
      GroupId: userInfo?.groupId,
    },
    data,
  };

  return httpClient.request(config).then((response) => {
    return {
      data: response.data.results.data,
      next: response.data.next,
    };
  });
};

const providerSearch = async (
  queryString: string,
  userInfo: IUserInfo,
  filter: ICostEstFilterParams,
  url?: string,
) => {
  const data = {
    memberId: userInfo?.memberId,
    groupId: userInfo?.groupId,
    divisionId: userInfo?.divisionId,
    planId: userInfo?.planId,
    network: DEFAULT_NETWORK_TYPE.NETWORK_NAME,
    lat: filter.targetLocation?.lat ?? "",
    lon: filter.targetLocation?.lng ?? "",
    distance: filter.distance?.value ?? null,
  };

  const config = {
    method: "post",
    maxBodyLength: Infinity,
    url: url
      ? url
      : `/free-text/${filter.procedure?.code}/filter/provider/?offset=0&limit=10&q=${queryString}`,
    headers: {
      Authorization: `Bearer ${userInfo?.accessToken}`,
      GroupId: userInfo?.groupId,
    },
    data,
  };

  return httpClient.request(config).then((response) => {
    return {
      data: response.data.results.data,
      next: response.data.next,
    };
  });
};

const specialitySearch = async (
  queryString: string,
  userInfo: IUserInfo,
): Promise<Speciality[]> => {
  const data = {
    memberId: userInfo.memberId,
    groupId: userInfo.groupId,
    divisionId: userInfo.divisionId,
    planId: userInfo.planId,
    network: DEFAULT_NETWORK_TYPE.NETWORK_NAME,
    lat: 42.36,
    lon: -71.1,
  };

  const config = {
    method: "post",
    maxBodyLength: Infinity,
    url: `/${queryString}/specialities`,
    headers: {
      Authorization: `Bearer ${userInfo.accessToken}`,
      GroupId: userInfo.groupId,
    },
    data,
  };
  return httpClient.request(config).then((response) => {
    return response.data.results || [];
  });
};

const getNetworkTiers = async (userInfo: IUserInfo) => {
  const data = {
    memberId: userInfo.memberId,
    groupId: userInfo.groupId,
    divisionId: userInfo.divisionId,
    planId: userInfo.planId,
  };
  const config = {
    method: "post",
    maxBodyLength: Infinity,
    url: `/tiers`,
    headers: {
      Authorization: `Bearer ${userInfo.accessToken}`,
      GroupId: userInfo.groupId,
    },
    data,
  };
  return httpClient.request(config).then((response) => {
    return response.data;
  });
};

const providerCostSearch = async (
  userInfo: IUserInfo,
  accumulators: SpendSummaryAccumulatorWithTier[],
  filter: ICostEstFilterParams,
  pageParam: number,
) => {
  // console.log("## CEDEbug = ", { accumulators });
  const data = {
    memberId: userInfo?.memberId,
    groupId: userInfo?.groupId,
    divisionId: userInfo?.divisionId,
    planId: userInfo?.planId,
    lat: filter?.targetLocation?.lat ?? "",
    lon: filter?.targetLocation?.lng ?? "",
    distance: filter?.distance?.value ?? 50,
    procedureCode: filter?.procedure?.code,
    network: DEFAULT_NETWORK_TYPE.NETWORK_NAME,
    ...(filter?.provider && { npi: filter?.provider.code }),
    ...(filter?.facility && { tin: filter?.facility.code }),

    specialities: filter?.specialities.map((speciality) => {
      return {
        specialityCode: speciality.categoryCode,
        taxonomies: speciality.taxonomies,
      };
    }),
    placeOfServices: filter?.pos.map((pos) => pos.code),
    networkTiers: accumulators.map((accumulator) => {
      const network = filter.networks?.filter(
        (network) => network.tier === accumulator.tierType,
      );
      if (!network) {
        return [];
      }
      return {
        deductible: accumulator.deductiblePaid,
        deductibleLimit: accumulator.deductibleLimit,
        oop: accumulator.OOPPaid,
        oopLimit: accumulator.OOPMax,
        tier: accumulator.tierType,
        networkNames: [...network.map((n) => n.networkName)],
      };
    }),
  };
  // console.log("@provider-search-pt data: ", { data, filter });
  const limit = filter?.limit ?? 10;

  const config = {
    method: "post",
    maxBodyLength: Infinity,
    url: `/providers/v2/cost?offset=${pageParam}&limit=${limit}&sort=+${filter?.sort}`,
    headers: {
      Authorization: `Bearer ${userInfo?.accessToken}`,
      GroupId: userInfo?.groupId,
    },
    data,
  };

  return httpClient.request(config).then((response) => {
    return {
      next: response.data.next,
      data: response.data.results,
    };
  });
};

export const benefitBreakdownSearch = async (userInfo: IUserInfo) => {
  const data = {
    memberId: userInfo?.memberId,
    groupId: userInfo?.groupId,
    divisionId: userInfo?.divisionId,
    planId: userInfo?.planId,
  };
  const config = {
    method: "post",
    maxBodyLength: Infinity,
    url: `provider/benefitBreakdown`,
    headers: {
      Authorization: `Bearer ${userInfo?.accessToken}`,
      GroupId: userInfo?.groupId,
    },
    data,
  };

  return httpClient.request(config).then((response) => {
    return response.data;
  });
};

export {
  procedureSearch,
  specialitySearch,
  getNetworkTiers,
  facilitySearch,
  providerSearch,
  providerCostSearch,
};
